import React, { useState } from 'react';
import { Stack, Text } from '@chakra-ui/react';
import { ItemModel } from 'models';
import { Condition } from 'models/types';
import { IoLocationOutline } from 'react-icons/io5';
import { FaDollarSign, FaClipboardList, FaStickyNote, FaCheckCircle, FaCheck } from 'react-icons/fa';
import Wizard from '../Wizard';
import ItemBidCard from 'components/Item/ItemBidCard/ItemBidCard';
import StepWrapper from '../StepWrapper';
import ProgressBar from '../../ProgressBar/ProgressBar';

interface ItemBidGuideWizardProps {
  item: ItemModel;
}

export default function ItemBidGuideWizard({ item }: ItemBidGuideWizardProps) {
  const [activeStep, setActiveStep] = useState(0);
  const amount =
    item.getEstimatedMarketPrice() ||
    item.getReleaseConvertedPrice() ||
    1000;

  const step1Content = (
    <Stack>
      <ItemBidCard
        item={item}
        bidAmount={amount}
        bidCreatedDays={0}
        userOwnsBid
        shorten
      />
      <StepWrapper
        stepKey="amount"
        icon={FaDollarSign}
        title="Step 1: Bid Amount"
        description="Specify your bid price"
        minH="260px"
      >
        <Text fontWeight={300} fontSize="sm" textAlign="left">
          Submit an offer for your desired amount. Our pricing engine can guide you on fair market value.
        </Text>
      </StepWrapper>
    </Stack >
  );

  const step2Content = (
    <Stack>
      <ItemBidCard
        item={item}
        bidAmount={amount}
        bidCreatedDays={0}
        condition={Condition.GOOD}
        userOwnsBid
        shorten
      />
      <StepWrapper
        stepKey="condition"
        icon={FaClipboardList}
        title="Step 2: Minimum Condition"
        description="Select the lowest acceptable condition"
        minH="260px"
      >
        <Text fontWeight={300} fontSize="sm" textAlign="left">
          Choose the minimum acceptable item condition for your offer.
        </Text>
      </StepWrapper>
    </Stack>
  );

  const step3Content = (
    <Stack>
      <ItemBidCard
        item={item}
        bidAmount={amount}
        bidCreatedDays={0}
        condition={Condition.GOOD}
        userOwnsBid
        shorten
      />
      <StepWrapper
        stepKey="notes"
        icon={FaStickyNote}
        title="Step 3: Add Notes"
        description="Optional notes for the seller"
        minH="260px"
      >
        <Text fontWeight={300} fontSize="sm" textAlign="left">
          Provide additional context about your bid if needed. Highlight any potential dealbreakers.
        </Text>
      </StepWrapper>
    </Stack>
  );

  const step4Content = (
    <Stack>
      <ItemBidCard
        item={item}
        bidAmount={amount}
        bidCreatedDays={0}
        condition={Condition.GOOD}
        countryCode={'US'}
        userOwnsBid
        shorten
      />
      <StepWrapper
        stepKey="address"
        icon={IoLocationOutline}
        title="Step 4: Delivery Address"
        description="Confirm delivery address"
        minH="260px"
      >
        <Text fontWeight={300} fontSize="sm" textAlign="left">
          Your primary address is selected by default but can be changed if needed.
        </Text>
      </StepWrapper>
    </Stack>
  );

  const step5Content = (
    <Stack>
      <ItemBidCard
        item={item}
        bidAmount={amount}
        bidCreatedDays={0}
        condition={Condition.GOOD}
        countryCode={'US'}
        userOwnsBid
        shorten
      />
      <StepWrapper
        stepKey="confirm"
        icon={FaCheckCircle}
        title="Step 5: Review & Submit"
        description="Confirm your bid"
        minH="260px"
      >
        <Stack spacing={4}>
          <Text fontWeight={300} fontSize="sm" textAlign="left">
            Review everything one last time before submitting your bid. You can edit your bid after placing it.
          </Text>
        </Stack>
      </StepWrapper>
    </Stack>
  );

  const wizardSteps = [
    { label: '', icon: FaDollarSign, content: step1Content },
    { label: '', icon: FaClipboardList, content: step2Content },
    { label: '', icon: FaStickyNote, content: step3Content },
    { label: '', icon: IoLocationOutline, content: step4Content },
    { label: '', icon: FaCheckCircle, content: step5Content, requiresValidation: true, isValidated: false },
  ];

  const totalSteps = wizardSteps.length;
  const progressPercentage = ((activeStep + 1) / totalSteps) * 100;
  const progressIcon = activeStep === totalSteps - 1 ? FaCheck : wizardSteps[activeStep].icon;

  return (
    <Stack width="100%">
      <ProgressBar progress={progressPercentage} icon={progressIcon} dynamicColor height={4} />
      <Wizard steps={wizardSteps} showStepper={false} onStepChange={setActiveStep} />
    </Stack>
  );
}
